import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import type { ImageGridImage, ImageGridMedia, ImageGridVideo, ImageGridPayload } from './types.ts';
import { useMarketingConsent } from '/Foundation/Common/code/Helpers/useMarketingConsent.ts';
import '/Foundation/Common/code/Views/Foundation/Common/ConsentPlaceholder.js';

// Lightbox is click-only — code-split it out of the entry chunk so it never
// competes with the gallery's first render. Webpack fetches the chunk on the
// first zoom interaction.
const ImageZoom = lazy(() => import('./ImageZoom.tsx'));

type Props = {
  payload: ImageGridPayload;
};

const MAX_AWARDS = 5;

// Splide breakpoints use max-width; matches the 992px gallery image breakpoint.
const MOBILE_SPLIDE_BREAKPOINT = 991;

// Mobile gallery swipe: keep drag off video player controls only.
const MOBILE_SPLIDE_NO_DRAG =
  'media-control-bar, media-volume-range, media-time-range, media-play-button, media-mute-button, media-fullscreen-button, media-settings-menu-button, .ytPlayBtn, thule-consent-placeholder button';

const TAP_MOVE_THRESHOLD = 8;

const buildYoutubeEmbedUrl = (url: string): string => {
  const base = url.split('?')[0];
  return `${base}?autoplay=1&mute=1&controls=1&rel=0&enablejsapi=1&playsinline=0`;
};

const isTapNotDrag = (start: { x: number; y: number } | null, event: { clientX: number; clientY: number }): boolean => {
  if (!start) return false;
  return (
    Math.abs(event.clientX - start.x) <= TAP_MOVE_THRESHOLD && Math.abs(event.clientY - start.y) <= TAP_MOVE_THRESHOLD
  );
};

// YouTube cannot use thule-video-player fullscreen — V1 image grid used a
// fullscreen iframe overlay instead (see ProductDetail_Images.cshtml).
const YoutubeOverlaySlide: React.FC<{ video: ImageGridVideo; consented: boolean }> = ({ video, consented }) => {
  const [open, setOpen] = useState(false);
  const embedUrl = buildYoutubeEmbedUrl(video.source);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('overflow-hidden');
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const trigger = triggerRef.current;
    return () => {
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onKeyDown);
      trigger?.focus();
    };
  }, [open]);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    pointerStart.current = { x: event.clientX, y: event.clientY };
  };

  const handlePointerCancel = () => {
    pointerStart.current = null;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // detail 0 = keyboard activation, which has no pointerdown to compare against.
    if (event.detail !== 0 && !isTapNotDrag(pointerStart.current, event)) {
      pointerStart.current = null;
      return;
    }
    pointerStart.current = null;
    setOpen(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="pdpGallery__youtubeTrigger"
        onPointerDown={handlePointerDown}
        onPointerCancel={handlePointerCancel}
        onClick={handleClick}
        aria-label="Play video"
      >
        {consented && video.posterUrl && (
          <img src={video.posterUrl} alt={video.altText ?? video.title ?? ''} loading="lazy" />
        )}
        <span className="pdpGallery__youtubePlay" aria-hidden="true">
          <span className="sr-only">Play video</span>
          <span className="pdpGallery__youtubePlayIcon">
            <i className="le-icon-play" />
          </span>
        </span>
      </button>
      {open &&
        createPortal(
          <div className="pdpYoutubeFullscreen" role="dialog" aria-modal="true" aria-label="Video player">
            <button
              ref={closeRef}
              type="button"
              className="btn-close btn-close-white pdpYoutubeFullscreen__close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            />
            {consented ? (
              <iframe
                src={embedUrl}
                title={video.title ?? video.altText ?? 'Video'}
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            ) : (
              React.createElement('thule-consent-placeholder')
            )}
          </div>,
          document.body
        )}
    </>
  );
};

// Poster click → fullscreen at every width, same as `_ManualsTab.cshtml`.
const VideoSlide: React.FC<{ video: ImageGridVideo; consented: boolean }> = ({ video, consented }) => {
  if (video.videoProvider === 'youtube') {
    return <YoutubeOverlaySlide video={video} consented={consented} />;
  }

  const props: Record<string, string | undefined> = {
    'poster-image': video.posterUrl,
    'click-fullscreen': 'true',
    'compact-player': 'true',
  };
  if (video.videoProvider === 'cloudflare') props['cloudflare-id'] = video.source;
  else props['direct-url'] = video.source;

  return React.createElement('thule-video-player', props);
};

const padIndex = (value: number): string => String(value).padStart(2, '0');

// Figma thumbnail strip: fade at overflow edges only (start after the list
// has moved, end while more items remain). Works for both axes.
const readThumbsFade = (el: HTMLElement): { start: boolean; end: boolean } => {
  const overflowY = el.scrollHeight - el.clientHeight;
  const overflowX = el.scrollWidth - el.clientWidth;
  if (overflowY > 1) return { start: el.scrollTop > 1, end: el.scrollTop < overflowY - 1 };
  if (overflowX > 1) return { start: el.scrollLeft > 1, end: el.scrollLeft < overflowX - 1 };
  return { start: false, end: false };
};

// Nudge only the strip so the page does not move with scrollIntoView.
const scrollThumbIntoHost = (host: HTMLElement, thumb: HTMLElement): void => {
  const hostRect = host.getBoundingClientRect();
  const thumbRect = thumb.getBoundingClientRect();
  if (thumbRect.top < hostRect.top) host.scrollTop -= hostRect.top - thumbRect.top;
  else if (thumbRect.bottom > hostRect.bottom) host.scrollTop += thumbRect.bottom - hostRect.bottom;
  if (thumbRect.left < hostRect.left) host.scrollLeft -= hostRect.left - thumbRect.left;
  else if (thumbRect.right > hostRect.right) host.scrollLeft += thumbRect.right - hostRect.right;
};

const ImageGrid: React.FC<Props> = ({ payload }) => {
  const items = payload.items ?? [];
  const badge = payload.badge;
  const awards = (payload.awards ?? []).slice(0, MAX_AWARDS);

  const mainRef = useRef<Splide>(null);
  const thumbsHostRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomImageIndex, setZoomImageIndex] = useState<number | null>(null);
  const [thumbsFade, setThumbsFade] = useState({ start: false, end: true });
  const consented = useMarketingConsent();

  type RoofRackFilter = { footType: string; generation: string };
  const [roofRackFilter, setRoofRackFilter] = useState<RoofRackFilter | null>(null);
  // Fitting videos for the selected solution. Null means no car is selected.
  const [solutionVideos, setSolutionVideos] = useState<ImageGridVideo[] | null>(null);

  useEffect(() => {
    if (!payload.isRoofRackProduct) return undefined;
    const onCarSelected = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const { footType, footGeneration, instructionalVideos } = detail;
      if (footType && footGeneration) {
        setRoofRackFilter({ footType, generation: footGeneration });
      }
      //temp disable pending design update
      //setSolutionVideos(Array.isArray(instructionalVideos) ? instructionalVideos : []);
    };
    const onCarReset = () => {
      setRoofRackFilter(null);
      setSolutionVideos(null);
    };
    window.addEventListener('roofRackCarSelected', onCarSelected);
    window.addEventListener('roofRackCarReset', onCarReset);
    return () => {
      window.removeEventListener('roofRackCarSelected', onCarSelected);
      window.removeEventListener('roofRackCarReset', onCarReset);
    };
  }, [payload.isRoofRackProduct]);

  const visibleItems = useMemo<ImageGridMedia[]>(() => {
    if (!payload.isRoofRackProduct) return items;

    const gallery = roofRackFilter
      ? items.filter((item) => {
          if (item.mediaType === 'video') {
            // Lifestyle and image-grid videos are not roof-specific.
            if (!item.isInstructional) return true;
            return item.footType === roofRackFilter.footType && item.generation === roofRackFilter.generation;
          }
          return item.footType === roofRackFilter.footType && item.generation === roofRackFilter.generation;
        })
      : items.filter((item) => item.mediaType === 'video' || item.isDefaultImageWhenNoCarSelected);

    if (!solutionVideos?.length) return gallery;

    const seen = new Set(gallery.map((item) => item.id));
    const extra = solutionVideos.filter((video) => !seen.has(video.id));
    return [...gallery, ...extra];
  }, [items, payload.isRoofRackProduct, roofRackFilter, solutionVideos]);

  useEffect(() => {
    setActiveIndex(0);
    mainRef.current?.go(0);
    const host = thumbsHostRef.current;
    if (host) {
      host.scrollTop = 0;
      host.scrollLeft = 0;
    }
  }, [visibleItems]);

  const imageItems = useMemo<ImageGridImage[]>(
    () => visibleItems.filter((item): item is ImageGridImage => item.mediaType === 'image'),
    [visibleItems]
  );

  const activeItem = visibleItems[activeIndex];
  const activeCaption =
    activeItem?.mediaType === 'image'
      ? activeItem.caption
      : activeItem?.mediaType === 'video'
        ? activeItem.title
        : undefined;

  const updateThumbsFade = useCallback(() => {
    const host = thumbsHostRef.current;
    if (!host) return;
    const next = readThumbsFade(host);
    setThumbsFade((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
  }, []);

  useLayoutEffect(() => {
    if (visibleItems.length <= 1) return undefined;
    const host = thumbsHostRef.current;
    if (!host) return undefined;
    updateThumbsFade();
    const observer = new ResizeObserver(updateThumbsFade);
    observer.observe(host);
    return () => observer.disconnect();
  }, [updateThumbsFade, visibleItems]);

  const openZoomFor = useCallback(
    (imageId: string) => {
      const idx = imageItems.findIndex((img) => img.id === imageId);
      if (idx >= 0) setZoomImageIndex(idx);
    },
    [imageItems]
  );

  useEffect(() => {
    const splide = mainRef.current?.splide;
    if (!splide) {
      return undefined;
    }

    const onSlideClick = (Slide: { index: number }) => {
      const item = visibleItems[Slide.index];
      if (item?.mediaType === 'image') openZoomFor(item.id);
    };

    const resetVideos = () => {
      splide.root?.querySelectorAll<HTMLElement>('thule-video-player').forEach((player) => {
        const media = player.querySelector<HTMLMediaElement>('hls-video, youtube-video, video');
        media?.pause?.();
      });
    };

    splide.on('click.imageGrid', onSlideClick);
    splide.on('move.imageGrid', resetVideos);
    return () => {
      splide.off('click.imageGrid');
      splide.off('move.imageGrid');
    };
  }, [visibleItems, openZoomFor]);

  // Synchronous inside the thumb click: requestFullscreen needs the user activation.
  const openVideoAt = (index: number) => {
    const splide = mainRef.current?.splide;
    splide?.go(index);
    splide?.Components.Slides.getAt(index)
      ?.slide.querySelector<HTMLButtonElement>('.thule-video-poster .ytPlayBtn, .pdpGallery__youtubeTrigger')
      ?.click();
  };

  if (visibleItems.length === 0) return null;

  const closeZoom = (lastImageIndex: number) => {
    setZoomImageIndex(null);
    const target = imageItems[lastImageIndex];
    if (!target) return;
    const mergedIndex = visibleItems.findIndex((it) => it.id === target.id);
    if (mergedIndex >= 0) mainRef.current?.go(mergedIndex);
  };

  const hasThumbs = visibleItems.length > 1;

  return (
    <div className={`pdpGallery${hasThumbs ? ' pdpGallery--withThumbs' : ''}`}>
      <div className="pdpGallery__primary">
        <div className="pdpGallery__main">
          <Splide
            ref={mainRef}
            aria-label="Product images"
            hasTrack={false}
            onMoved={(_splide, newIndex) => {
              setActiveIndex(newIndex);
              const host = thumbsHostRef.current;
              const thumb = host?.querySelector<HTMLElement>(`[data-thumb-index="${newIndex}"]`);
              if (host && thumb) scrollThumbIntoHost(host, thumb);
              updateThumbsFade();
            }}
            options={{
              type: 'slide',
              rewind: false,
              pagination: false,
              arrows: false,
              heightRatio: 1,
              drag: false,
              breakpoints: {
                [MOBILE_SPLIDE_BREAKPOINT]: {
                  drag: true,
                  noDrag: MOBILE_SPLIDE_NO_DRAG,
                },
              },
            }}
          >
            <SplideTrack>
              {visibleItems.map((item, index) =>
                item.mediaType === 'image' ? (
                  <SplideSlide key={item.id}>
                    <button
                      type="button"
                      className="pdpGallery__zoomTrigger"
                      onClick={() => openZoomFor(item.id)}
                      aria-label="Zoom image"
                    >
                      <picture>
                        <source media="(min-width:992px)" srcSet={item.assetUrlLarge} />
                        <img
                          src={item.assetUrlSmall}
                          alt={item.altText ?? ''}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : undefined}
                        />
                      </picture>
                    </button>
                  </SplideSlide>
                ) : (
                  <SplideSlide key={item.id} className="pdpGallery__videoSlide">
                    <VideoSlide video={item} consented={consented} />
                  </SplideSlide>
                )
              )}
            </SplideTrack>
          </Splide>

          <div className="pdpGallery__overlay">
            {activeIndex === 0 && badge?.title && (
              <div
                className={`pdpGallery__badge badge--${badge.categoryId} paragraph--xs`}
                data-badgetype={badge.categoryId}
              >
                {badge.title}
              </div>
            )}

            {activeCaption && (
              <p className="pdpGallery__caption paragraph--xs" aria-live="polite">
                {activeCaption}
              </p>
            )}

            {activeIndex === 0 && awards.length > 0 && (
              <div className="pdpGallery__awards">
                {awards.map((award, index) => {
                  const img = (
                    <img
                      src={award.imageUrl}
                      alt={award.tooltip ?? award.description ?? ''}
                      title={award.tooltip}
                      loading="lazy"
                    />
                  );
                  return award.url ? (
                    <a
                      key={`award-${index}`}
                      className="pdpGallery__award"
                      href={award.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {img}
                    </a>
                  ) : (
                    <div key={`award-${index}`} className="pdpGallery__award">
                      {img}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {hasThumbs && (
          <div className="pdpGallery__footer">
            <p className="pdpGallery__count paragraph--xs">
              {padIndex(activeIndex + 1)} / {padIndex(visibleItems.length)}
            </p>
            <div className="pdpGallery__arrows">
              <button
                type="button"
                className="pdpGallery__arrow"
                disabled={activeIndex === 0}
                onClick={() => mainRef.current?.go('-')}
              >
                <i className="le-icon-chevron" aria-hidden="true"></i>
                <span className="sr-only">Previous image</span>
              </button>
              <button
                type="button"
                className="pdpGallery__arrow"
                disabled={activeIndex === visibleItems.length - 1}
                onClick={() => mainRef.current?.go('+')}
              >
                <i className="le-icon-chevron rot-180" aria-hidden="true"></i>
                <span className="sr-only">Next image</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {hasThumbs && (
        <div
          className={`pdpGallery__thumbs${thumbsFade.start ? ' pdpGallery__thumbs--fadeStart' : ''}${thumbsFade.end ? ' pdpGallery__thumbs--fadeEnd' : ''}`}
        >
          <div
            className="pdpGallery__thumbsList"
            aria-label="Product image thumbnails"
            ref={thumbsHostRef}
            onScroll={updateThumbsFade}
          >
            {visibleItems.map((item, index) => {
              const isVideo = item.mediaType === 'video';
              const src =
                item.mediaType === 'image'
                  ? item.thumbnailUrl
                  : item.videoProvider === 'youtube' && !consented
                    ? undefined
                    : item.posterUrl;
              const alt = item.mediaType === 'image' ? (item.altText ?? '') : (item.title ?? item.altText ?? '');
              return (
                <button
                  key={`thumb-${item.id}`}
                  type="button"
                  data-thumb-index={index}
                  className={`pdpGallery__thumb${isVideo ? ' pdpGallery__thumb--video' : ''}${index === activeIndex ? ' pdpGallery__thumb--selected' : ''}`}
                  onClick={() => (isVideo ? openVideoAt(index) : mainRef.current?.go(index))}
                >
                  {/* Not lazy: the SSR strip already fetched these, and deferring
                      again flashes empty thumbs on mount. */}
                  {src && <img src={src} alt={alt} />}
                  {isVideo && (
                    <span className="pdpGallery__thumbPlay" aria-hidden="true">
                      <span className="pdpGallery__thumbPlayIcon">
                        <i className="le-icon-play"></i>
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {zoomImageIndex !== null && (
        /* No fallback UI: while the chunk loads, the gallery stays visible as-is. */
        <Suspense fallback={null}>
          <ImageZoom items={imageItems} startIndex={zoomImageIndex} onClose={closeZoom} />
        </Suspense>
      )}
    </div>
  );
};

export default ImageGrid;
